import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map } from 'rxjs';

import { SegmentTrackEvent } from '@ninety/platform/models';
import { SegmentActions } from '@ninety/platform/state/app-global/analytics/segment/segment.actions';

import { SearchResultItemType } from './global-search-api.model';
import { GlobalSearchComponentActions, GlobalSearchEffectActions } from './global-search-state.actions';

const ENTITY_TYPE_MAP = {
  [SearchResultItemType.rocks]: 'rock',
  [SearchResultItemType.issues]: 'issue',
  [SearchResultItemType.milestones]: 'milestone',
  [SearchResultItemType.todos]: 'todo',
};

@Injectable()
export class GlobalSearchAnalyticsEffects {
  private actions$ = inject(Actions);

  trackBeginSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalSearchComponentActions.beginSearch),
      map(({ query }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.GLOBAL_SEARCH_NEW_SEARCH,
          params: {
            query,
          },
        })
      )
    )
  );

  trackSearchCleared$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalSearchEffectActions.searchCleared),
      map(() =>
        SegmentActions.track({
          event: SegmentTrackEvent.GLOBAL_SEARCH_CLEARED,
        })
      )
    )
  );

  trackSearchLoadMore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalSearchComponentActions.loadMoreResults),
      map(() =>
        SegmentActions.track({
          event: SegmentTrackEvent.GLOBAL_SEARCH_LOAD_MORE,
        })
      )
    )
  );

  trackSearchNavigateToItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalSearchEffectActions.openSearchResultItem),
      map(({ item }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.GLOBAL_SEARCH_NAVIGATE_TO_ITEM,
          params: {
            entityType: ENTITY_TYPE_MAP[item.itemType] ?? 'search',
          },
        })
      )
    )
  );

  trackSearchError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalSearchEffectActions.searchFailure, GlobalSearchEffectActions.loadMoreResultsFailure),
      map(({ error }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.GLOBAL_SEARCH_ERROR,
          params: {
            error: error instanceof Error ? error.message : String(error),
          },
        })
      )
    )
  );

  trackUnknownItemType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalSearchEffectActions.unknownSearchResultItemType),
      map(({ item }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.GLOBAL_SEARCH_UNKNOWN_ITEM_TYPE,
          params: {
            itemType: item?.itemType,
          },
        })
      )
    )
  );
}
